import { useEffect } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router";
import {
    Container,
    Typography,
    Stack,
    Divider,
    Button,
    Grid,
    Card,
    CardContent,
    Box,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    useTheme,
} from "@mui/material";
import SaveIcon from "@mui/icons-material/Save";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useTranslation } from "react-i18next";
import { useSnackbar } from "src/utils/useSnackbar";
import { TagValueFormData } from "src/types/tag";
import { useGetAllTagTypes, useGetOneTagValue, useCreateTagValue, useUpdateTagValue } from "src/api/client-api/tag";
import { URLHandler } from "src/services/URLHandler";

const tagValueSchema = z.object({
    name: z
        .string()
        .min(1, "Le nom est requis")
        .max(255, "Le nom ne peut pas dépasser 255 caractères")
        .regex(/^[^,]*$/, "String cannot contain a comma"),
    description: z.string().optional(),
    color: z.string().optional(),
    sortOrder: z.number().min(0, "L'ordre doit être positif"),
    tagTypeId: z.string().min(1, "Le type de tag est requis"),
});

type TagValueFormValues = z.infer<typeof tagValueSchema>;

export const TagValueForm = () => {
    const { t } = useTranslation();
    const { tagValueId } = useParams<{ tagValueId: string }>();
    const navigate = useNavigate();
    const snackbar = useSnackbar();
    const [searchParams] = useSearchParams();
    const preselectedTagTypeId = searchParams.get("tagType");

    const isEditing = Boolean(tagValueId);

    // Récupération des types de tags
    const { tagTypes = [] } = useGetAllTagTypes({ itemsPerPage: 100, active: true });

    // Récupération de la valeur de tag en mode édition
    const { data: tagValue } = useGetOneTagValue(isEditing ? tagValueId : undefined);
    const theme = useTheme();

    // Mutations pour créer/modifier
    const createMutation = useCreateTagValue();
    const updateMutation = useUpdateTagValue();

    const {
        control,
        handleSubmit,
        watch,
        reset,
        formState: { errors },
    } = useForm<TagValueFormValues>({
        resolver: zodResolver(tagValueSchema),
        defaultValues: {
            name: "",
            description: "",
            color: "#000000",
            sortOrder: 0,
            tagTypeId: preselectedTagTypeId || "",
        },
    });

    const isSubmitting = createMutation.isLoading || updateMutation.isLoading;

    // Mettre à jour le formulaire quand les données sont chargées
    useEffect(() => {
        if (tagValue) {
            reset({
                name: tagValue.name || "",
                description: tagValue.description || "",
                color: tagValue.color || "",
                sortOrder: tagValue.sortOrder || 0,
                tagTypeId: tagValue.tagType?.id || "",
            });
        }
    }, [tagValue, reset]);

    const selectedTagTypeId = watch("tagTypeId");
    const selectedTagType = tagTypes.find((tt) => tt.id === selectedTagTypeId);

    // Fonction pour gérer la soumission
    const handleSubmitForm = (data: TagValueFormData) => {
        if (isEditing && tagValueId) {
            updateMutation.mutate(
                { tagValueId, tagValue: data },
                {
                    onSuccess: () => {
                        snackbar.setSnackbarProps({
                            message: t(
                                "tags.messages.tag_value_updated_successfully",
                                "Valeur de tag mise à jour avec succès",
                            ),
                            type: "success",
                        });
                        navigate(URLHandler.settings.tags.tagValues.list());
                    },
                    onError: () => {
                        snackbar.setSnackbarProps({
                            message: t(
                                "tags.messages.failed_to_update_tag_value",
                                "Erreur lors de la mise à jour de la valeur de tag",
                            ),
                            type: "error",
                        });
                    },
                },
            );
        } else {
            createMutation.mutate(data, {
                onSuccess: () => {
                    snackbar.setSnackbarProps({
                        message: t("tags.messages.tag_value_created_successfully", "Valeur de tag créée avec succès"),
                        type: "success",
                    });
                    navigate(URLHandler.settings.tags.tagValues.list());
                },
                onError: () => {
                    snackbar.setSnackbarProps({
                        message: t(
                            "tags.messages.failed_to_create_tag_value",
                            "Erreur lors de la création de la valeur de tag",
                        ),
                        type: "error",
                    });
                },
            });
        }
    };

    const onSubmit = (data: TagValueFormValues) => {
        handleSubmitForm(data);
    };

    const handleGoBack = () => {
        navigate(URLHandler.settings.tags.tagValues.list());
    };

    const allColorsPreset = theme.custom.dataviz.onlyMainFromAll;
    return (
        <Container
            component="main"
            maxWidth={false}
            sx={{
                m: "0",
                p: "5rem !important",
            }}
        >
            <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
                <Button
                    startIcon={<ArrowBackIcon />}
                    onClick={handleGoBack}
                    variant="outlined"
                    sx={(theme) => ({
                        border: `1px solid ${theme.palette.primary[600]}`,
                        color: theme.palette.primary[600],
                        textTransform: "none",
                        "&:hover": {
                            border: `1px solid ${theme.palette.primary[700]}`,
                            color: theme.palette.primary[700],
                            backgroundColor: "transparent",
                        },
                    })}
                >
                    {t("common.actions.back", "Retour")}
                </Button>
                <Typography component="h1" variant="h2">
                    {isEditing ?
                        t("tags.titles.edit_tag_value", "Modifier la valeur de tag")
                    :   t("tags.titles.new_tag_value", "Nouvelle valeur de tag")}
                </Typography>
            </Stack>

            <Divider sx={{ mb: 4 }} />

            <form onSubmit={handleSubmit(onSubmit)}>
                <Grid container spacing={4}>
                    <Grid item xs={12} md={8}>
                        <Card elevation={0}>
                            <CardContent>
                                <Typography variant="h6" sx={{ mb: 3 }}>
                                    {t("tags.form.sections.general_information", "Informations générales")}
                                </Typography>

                                <Stack spacing={3}>
                                    <Controller
                                        name="tagTypeId"
                                        control={control}
                                        render={({ field }) => (
                                            <FormControl fullWidth error={Boolean(errors.tagTypeId)}>
                                                <InputLabel>
                                                    {t("tags.form.fields.tag_type", "Type de tag")} *
                                                </InputLabel>
                                                <Select
                                                    {...field}
                                                    label={t("tags.form.fields.tag_type", "Type de tag")}
                                                    disabled={isEditing}
                                                >
                                                    {tagTypes.map((tagType) => (
                                                        <MenuItem key={tagType.id} value={tagType.id}>
                                                            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                                                <Box
                                                                    sx={{
                                                                        width: 16,
                                                                        height: 16,
                                                                        backgroundColor: tagType.color,
                                                                        borderRadius: 1,
                                                                    }}
                                                                />
                                                                {tagType.name}
                                                            </Box>
                                                        </MenuItem>
                                                    ))}
                                                </Select>
                                                {errors.tagTypeId && (
                                                    <Typography variant="caption" color="error">
                                                        {errors.tagTypeId.message}
                                                    </Typography>
                                                )}
                                            </FormControl>
                                        )}
                                    />

                                    <Controller
                                        name="name"
                                        control={control}
                                        render={({ field }) => (
                                            <TextField
                                                {...field}
                                                label={t("tags.form.fields.name", "Nom *")}
                                                fullWidth
                                                error={Boolean(errors.name)}
                                                helperText={errors.name?.message}
                                            />
                                        )}
                                    />

                                    <Controller
                                        name="description"
                                        control={control}
                                        render={({ field }) => (
                                            <TextField
                                                {...field}
                                                label={t("tags.form.fields.description", "Description")}
                                                fullWidth
                                                multiline
                                                rows={3}
                                                error={Boolean(errors.description)}
                                                helperText={errors.description?.message}
                                            />
                                        )}
                                    />

                                    <Controller
                                        name="color"
                                        control={control}
                                        render={({ field }) => (
                                            <TextField
                                                {...field}
                                                label={t("tags.form.fields.color", "Couleur personnalisée")}
                                                type="color"
                                                fullWidth
                                                error={Boolean(errors.color)}
                                                helperText={
                                                    errors.color?.message ||
                                                    "Laisser vide pour utiliser la couleur du type de tag"
                                                }
                                            />
                                        )}
                                    />
                                    <Stack flexDirection="row" gap={theme.spacing(2)}>
                                        <Controller
                                            name="color"
                                            control={control}
                                            render={({ field }) => (
                                                <Box>
                                                    <Button
                                                        variant="primary"
                                                        onClick={() => {
                                                            field.onChange("#000000");
                                                        }}
                                                    >
                                                        {"Reset color"}
                                                    </Button>
                                                </Box>
                                            )}
                                        />
                                        <Controller
                                            name="color"
                                            control={control}
                                            render={({ field }) => {
                                                return (
                                                    <Stack direction="row" spacing={2} alignItems="center">
                                                        {allColorsPreset.map((color) => {
                                                            return (
                                                                <Box
                                                                    key={color}
                                                                    sx={{
                                                                        width: "20px",
                                                                        height: "20px",
                                                                        backgroundColor: color,
                                                                        border: "1px solid #ccc",
                                                                        borderRadius: 12,
                                                                    }}
                                                                    onClick={() => {
                                                                        field.onChange(color);
                                                                    }}
                                                                />
                                                            );
                                                        })}
                                                    </Stack>
                                                );
                                            }}
                                        />
                                    </Stack>

                                    <Controller
                                        name="sortOrder"
                                        control={control}
                                        render={({ field }) => (
                                            <TextField
                                                {...field}
                                                label={t("tags.form.fields.sort_order", "Ordre de tri")}
                                                type="number"
                                                fullWidth
                                                error={Boolean(errors.sortOrder)}
                                                helperText={errors.sortOrder?.message}
                                                onChange={(e) => field.onChange(parseInt(e.target.value) || 0)}
                                            />
                                        )}
                                    />
                                </Stack>
                            </CardContent>
                        </Card>
                    </Grid>

                    <Grid item xs={12} md={4}>
                        <Card elevation={0}>
                            <CardContent>
                                <Typography variant="h6" sx={{ mb: 3 }}>
                                    {t("tags.form.sections.preview", "Aperçu")}
                                </Typography>

                                {selectedTagType && (
                                    <Stack spacing={2}>
                                        <Box>
                                            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                                {t("tags.form.fields.tag_type", "Type de tag")}
                                            </Typography>
                                            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                                <Box
                                                    sx={{
                                                        width: 16,
                                                        height: 16,
                                                        backgroundColor: selectedTagType.color,
                                                        borderRadius: 1,
                                                    }}
                                                />
                                                <Typography variant="body1">{selectedTagType.name}</Typography>
                                            </Box>
                                        </Box>

                                        <Box>
                                            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                                {t("tags.form.fields.context", "Contexte")}
                                            </Typography>
                                            <Typography variant="body1" sx={{ textTransform: "capitalize" }}>
                                                {selectedTagType.context}
                                            </Typography>
                                        </Box>
                                    </Stack>
                                )}
                            </CardContent>
                        </Card>

                        <Box sx={{ mt: 3 }}>
                            <Button
                                type="submit"
                                variant="contained"
                                fullWidth
                                size="large"
                                startIcon={<SaveIcon />}
                                disabled={isSubmitting}
                                sx={(theme) => ({
                                    background: `linear-gradient(180deg, ${theme.palette.primary[500]} 0%, ${theme.palette.primary[600]} 100%)`,
                                    color: "white !important",
                                    textTransform: "none",
                                    borderRadius: "44px",
                                    padding: "0px 20px",
                                    height: "40px",
                                    fontSize: "0.875rem",
                                    fontWeight: 700,
                                    lineHeight: "1.5em",
                                    boxShadow: "0px 1px 2px 0px rgba(42, 43, 39, 0.02)",
                                    "&:hover": {
                                        background: `linear-gradient(180deg, ${theme.palette.primary[600]} 0%, ${theme.palette.primary[700]} 100%)`,
                                        boxShadow: "0px 1px 3px 0px rgba(42, 43, 39, 0.03)",
                                    },
                                    "&:disabled": {
                                        background: `linear-gradient(180deg, ${theme.palette.primary[500]} 0%, ${theme.palette.primary[600]} 100%)`,
                                        opacity: 0.36,
                                        color: "white !important",
                                    },
                                })}
                            >
                                {isSubmitting ?
                                    t("common.actions.saving", "Enregistrement...")
                                : isEditing ?
                                    t("common.actions.update", "Mettre à jour")
                                :   t("common.actions.create", "Créer")}
                            </Button>
                        </Box>
                    </Grid>
                </Grid>
            </form>
        </Container>
    );
};
